import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Lock, LogIn, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CryptoJS from 'crypto-js'; // Librería para encriptación AES

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Clave secreta para encriptación AES del token (¡CÁMBIALA por una segura y compártela con el backend!)
  const SECRET_KEY = 'mi-clave-secreta-massi-store-2024';

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Encripta la contraseña con AES antes de enviar
      const encryptedPassword = password;// CryptoJS.AES.encrypt(password, SECRET_KEY).toString();

      // URL de tu API real (¡CÁMBIALA por la tuya!)
      const API_LOGIN_URL = 'https://appwebtienda-a6b3ecgnggbjh6c7.centralus-01.azurewebsites.net/api/autenticacion/validar'; // Placeholder - reemplaza con tu endpoint

      // Llama a la API real
      const response = await fetch(API_LOGIN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          usuario: username, 
          clave: encryptedPassword // Envía la clave encriptada
        }),
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        // Si no hay JSON válido, usa simulación para pruebas
        data = await simulateApiResponse(username, password);
      }

      // Maneja la respuesta según tu descripción
      if (response.ok && data !== null && data.usuario && data.token) {
        // Éxito: encripta y guarda el token de forma segura en localStorage
        const encryptedToken = CryptoJS.AES.encrypt(data.token, SECRET_KEY).toString();
        localStorage.setItem('encryptedToken', encryptedToken);
        localStorage.setItem('userData', JSON.stringify(data.usuario)); // Guarda datos del usuario (no sensibles)

        // Pasa el usuario completo y token desencriptado al estado de la app
        const decryptedToken = CryptoJS.AES.decrypt(encryptedToken, SECRET_KEY).toString(CryptoJS.enc.Utf8);
        onLogin(true, data.usuario, decryptedToken);
        navigate('/'); // Redirige al dashboard
      } else {
        // Error: null o sin campos requeridos
        setError(data?.message || 'Credenciales inválidas o error en la autenticación.');
      }
    } catch (err) {
      setError('Error de conexión. Verifica que la API esté disponible.');
      console.error('Error al intentar iniciar sesión:', err);
    } finally {
      setLoading(false);
    }
  };

  // Función temporal de simulación (elimínala cuando uses la API real)
  const simulateApiResponse = async (username, password) => {
    return new Promise(resolve => setTimeout(() => {
      if (username === 'admin' && password === 'password') {
        // Simula respuesta exitosa con estructura que describes
        resolve({
          usuario: {
            idCliente: '1',
            dniCliente: '12345678',
            rucCliente: '20123456789',
            apePaterno: 'Pérez',
            apeMaterno: 'García',
            nomPrimero: 'Juan',
            nomSegundo: 'Carlos',
            razonSocial: 'Juan Pérez García E.I.R.L.',
            razonComercial: 'Massi Store',
            fechaNacimiento: '1990-05-15',
            tlfCasa: '01-234-5678',
            tlfCelular: '987654321',
            email: 'juan.perez@massi-store.com',
            direccion: 'Av. Siempre Viva 742, Lima'
          },
          token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6Ikp1YW4gUGVyZXoiLCJpYXQiOjE1MTYyMzkwMjJ9.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c' // Token de ejemplo (JWT simulado)
        });
      } else {
        resolve(null); // Simula error
      }
    }, 1500)); // Simula retraso de red
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
      <motion.div
        className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-3xl p-8 md:p-12 shadow-2xl w-full max-w-md"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="flex flex-col items-center mb-8">
          <motion.div
            className="p-4 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full mb-4"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <LogIn className="w-10 h-10 text-white" />
          </motion.div>
          <h1 className="text-4xl font-extrabold text-gray-900 bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
            Bienvenido a Massi Store
          </h1>
          <p className="text-gray-500 mt-2 text-center">Inicia sesión para gestionar tu tienda de ropa</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="username">
              Usuario
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                id="username"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition-all duration-300 text-gray-900"
                placeholder="Tu nombre de usuario"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">
              Contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                id="password"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition-all duration-300 text-gray-900"
                placeholder="Tu contraseña secreta"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
            </div>
          </div>

          {error && (
            <motion.p
              className="text-red-600 text-sm text-center bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-center gap-2"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <AlertCircle className="w-4 h-4" />
              {error}
            </motion.p>
          )}

          <motion.button
            type="submit"
            className={`w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold py-3 px-4 rounded-xl shadow-lg transition-all duration-300 transform flex items-center justify-center gap-2 ${
              loading ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-xl hover:scale-105'
            }`}
            whileHover={loading ? {} : { scale: 1.02 }}
            whileTap={loading ? {} : { scale: 0.98 }}
            disabled={loading}
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Iniciando...
              </>
            ) : (
              <>
                <LogIn className="w-5 h-5" />
                Iniciar Sesión
              </>
            )}
          </motion.button>
        </form>
      </motion.div>
    </div>
  );
};

export default Login;